import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, ExternalLink, RefreshCw } from 'lucide-react'
import { ArtworkStateBadge, MatchBadge, ReviewBadge } from '../components/ExternalBadges'
import {
  useExternalArtist,
  useExternalArtworks,
  useLinkArtist,
  useOurArtists,
  useProviders,
  useQueueArtworks,
  useResolveCandidate,
  useReviewArtist,
  useSyncArtist,
  useUnlinkArtist,
  useUnqueueArtworks,
} from '../hooks/useExternalCatalog'
import type { CoverageFilter, ExternalArtwork, ReviewStatus } from '../api/externalCatalogApi'

const FILTERS: Array<{ value: CoverageFilter; label: string }> = [
  { value: 'missing', label: 'Missing' },
  { value: 'review', label: 'Needs review' },
  { value: 'have', label: 'We hold' },
  { value: 'all', label: 'All' },
]

/**
 * Can this artwork be asked for? Not if we hold it, it is in flight, its license ended, or it
 * looks like one of ours and nobody has yet said whether it is.
 */
const queueable = (a: ExternalArtwork) =>
  !a.artworkId &&
  !a.candidateArtworkId &&
  !a.expiredAt &&
  ['new', 'failed', 'skipped'].includes(a.ingestStatus ?? 'new')

/** Pick one of OUR artists to link, for when the name match found nobody or the wrong person. */
function ManualLink({ onPick, disabled }: { onPick: (artistId: number) => void; disabled: boolean }) {
  const [q, setQ] = useState('')
  const { data, isFetching } = useOurArtists(q)
  return (
    <div className="space-y-2">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Find one of our artists by name…"
        className="max-w-sm"
      />
      {q.trim().length >= 2 && !isFetching && data?.artists.length === 0 && (
        <p className="text-xs text-slate-500">No ARTHAUS artist by that name.</p>
      )}
      <div className="flex flex-wrap gap-2">
        {data?.artists.map((a) => (
          <Button key={a.id} size="sm" variant="outline" disabled={disabled} onClick={() => onPick(a.id)}>
            Link to {a.artisticName}
          </Button>
        ))}
      </div>
    </div>
  )
}

export function ExternalArtistPage() {
  const navigate = useNavigate()
  const { provider = '', externalId = '' } = useParams<{ provider: string; externalId: string }>()
  const [filter, setFilter] = useState<CoverageFilter>('missing')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data: providerData } = useProviders()
  const providerInfo = providerData?.providers.find((p) => p.key === provider)
  const { data, isLoading, isError } = useExternalArtist(provider, externalId)
  const { data: artworkData, isLoading: artworksLoading } = useExternalArtworks(provider, externalId, filter)

  const sync = useSyncArtist(provider, externalId)
  const link = useLinkArtist(provider, externalId)
  const unlink = useUnlinkArtist(provider, externalId)
  const review = useReviewArtist(provider, externalId)
  const queue = useQueueArtworks(provider, externalId)
  const unqueue = useUnqueueArtworks(provider, externalId)
  const resolve = useResolveCandidate(provider, externalId)

  const artworks = useMemo(() => artworkData?.artworks ?? [], [artworkData])
  const selectable = useMemo(() => artworks.filter(queueable), [artworks])

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-sm text-slate-600">
          This artist is not in the local mirror yet. Sync the catalog's artist roster first, then
          open them again.
        </p>
        <Button variant="outline" onClick={() => navigate('/app/external')}>
          Back to external catalogs
        </Button>
      </div>
    )
  }

  const { artist, ours, coverage } = data
  const linked = artist.matchStatus === 'linked'
  const label = providerInfo?.label ?? provider

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const submitQueue = () =>
    queue.mutate([...selected], { onSuccess: () => setSelected(new Set()) })

  const setReview = (reviewStatus: ReviewStatus) => review.mutate({ reviewStatus })

  return (
    <div className="space-y-6 p-6">
      <div>
        <Link to="/app/external" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="mr-1 h-4 w-4" />
          External catalogs
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{artist.name}</h1>
            <p className="text-sm text-slate-500">
              {[label, artist.username ? `@${artist.username}` : null, artist.country, `#${artist.externalId}`]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <MatchBadge artist={{ ...artist, artistName: ours?.artisticName, suggestedArtistName: ours?.artisticName }} />
              {!linked && <ReviewBadge status={artist.reviewStatus} />}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {artist.url && (
              <Button variant="outline" asChild>
                <a href={artist.url} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View on {label}
                </a>
              </Button>
            )}
            <Button onClick={() => sync.mutate()} disabled={sync.isPending || !providerInfo?.configured}>
              <RefreshCw className={`mr-2 h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
              {sync.isPending ? 'Syncing…' : 'Sync artworks'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">In ARTHAUS</CardTitle>
            <CardDescription>
              {linked
                ? 'Confirmed as one of our artists. Coverage below compares their catalog here with ours.'
                : artist.matchStatus === 'suggested'
                  ? 'A name match, not a confirmed one. Two people can share a name — check before linking.'
                  : 'Not linked to any of our artists.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {ours && (
              <div className="rounded-md border p-3 text-sm">
                <Link to={`/app/artists/${ours.id}`} className="font-medium text-blue-700 hover:underline">
                  {ours.artisticName}
                </Link>
                <div className="text-xs text-slate-500">
                  {ours.email} · {ours.artworks?.toLocaleString() ?? 0} artworks with us
                </div>
              </div>
            )}
            {linked ? (
              <Button variant="outline" size="sm" disabled={unlink.isPending} onClick={() => unlink.mutate(false)}>
                Remove link
              </Button>
            ) : (
              <>
                {artist.matchStatus === 'suggested' && ours && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" disabled={link.isPending} onClick={() => link.mutate(ours.id)}>
                      Yes, this is {ours.artisticName}
                    </Button>
                    <Button variant="outline" size="sm" disabled={unlink.isPending} onClick={() => unlink.mutate(true)}>
                      Not the same person
                    </Button>
                  </div>
                )}
                <ManualLink disabled={link.isPending} onPick={(artistId) => link.mutate(artistId)} />
              </>
            )}
          </CardContent>
        </Card>

        {linked ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Coverage</CardTitle>
              <CardDescription>
                {artist.artworksSyncedAt
                  ? `Artworks last synced ${new Date(artist.artworksSyncedAt).toLocaleString()}.`
                  : 'Artworks have not been fully synced yet — these figures are a floor.'}{' '}
                "We hold" is decided by comparing the images, not the titles. Look-alikes it is
                not sure about wait under "Needs review" and cannot be queued until answered.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-3 gap-4 text-sm">
                {[
                  { label: `In ${label}`, value: coverage.mirrored },
                  { label: 'We hold', value: coverage.have },
                  { label: 'Missing', value: coverage.missing },
                  { label: 'Needs review', value: coverage.review },
                  { label: 'Queued', value: coverage.queued },
                  { label: 'Failed', value: coverage.failed },
                  { label: 'License ended', value: coverage.expired },
                ].map((c) => (
                  <div key={c.label}>
                    <dt className="text-xs text-slate-500">{c.label}</dt>
                    <dd className="text-xl font-semibold tabular-nums">{c.value.toLocaleString()}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Do we want this artist?</CardTitle>
              <CardDescription>
                Curation only — approving here does not create an account or fetch any files.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-1">
                {(['new', 'shortlisted', 'approved', 'declined'] as ReviewStatus[]).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={artist.reviewStatus === s ? 'default' : 'outline'}
                    disabled={review.isPending}
                    onClick={() => setReview(s)}
                  >
                    {s === 'new' ? 'Unreviewed' : s[0].toUpperCase() + s.slice(1)}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-1 text-sm">
                <span className="mr-1 text-slate-500">If we add them:</span>
                {[
                  { value: 'announce' as const, label: 'Announce in the new-artist email' },
                  { value: 'catalog' as const, label: 'Add to the catalog quietly' },
                ].map((p) => (
                  <Button
                    key={p.value}
                    size="sm"
                    variant={artist.priority === p.value ? 'default' : 'outline'}
                    disabled={review.isPending}
                    onClick={() => review.mutate({ priority: artist.priority === p.value ? null : p.value })}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              {artist.biography && (
                <p className="line-clamp-6 whitespace-pre-line text-sm text-slate-600">{artist.biography}</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">
              Artworks{artist.artworkCount != null ? ` · ${artist.artworkCount.toLocaleString()} in ${label}` : ''}
            </CardTitle>
            {linked && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!selectable.length}
                  onClick={() =>
                    setSelected(
                      selected.size === selectable.length
                        ? new Set()
                        : new Set(selectable.map((a) => a.externalId)),
                    )
                  }
                >
                  {selected.size && selected.size === selectable.length ? 'Clear selection' : 'Select all shown'}
                </Button>
                <Button size="sm" disabled={!selected.size || queue.isPending} onClick={submitQueue}>
                  Queue {selected.size || ''} for ingest
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <Button
                key={f.value}
                size="sm"
                variant={filter === f.value ? 'default' : 'ghost'}
                onClick={() => {
                  setFilter(f.value)
                  setSelected(new Set())
                }}
              >
                {f.label}
              </Button>
            ))}
          </div>
          {linked && (
            <p className="text-xs text-slate-500">
              Queueing asks for the full-resolution print file. {label} licenses and invoices each
              file it hands over, so only queue work we intend to sell.
              {providerInfo && !providerInfo.downloadsEnabled
                ? ' Downloads are disabled in this environment: queued work waits until they are enabled.'
                : ''}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {artworksLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square w-full" />
              ))}
            </div>
          ) : !artworks.length ? (
            <p className="py-10 text-center text-sm text-slate-500">
              {coverage.mirrored === 0
                ? 'No artworks mirrored for this artist yet. Sync artworks to pull their catalog.'
                : filter === 'missing'
                  ? 'Nothing missing — everything mirrored for this artist is held, in review or expired.'
                  : 'No artworks in this view.'}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {artworks.map((a) => {
                const canQueue = linked && queueable(a)
                const isSelected = selected.has(a.externalId)
                return (
                  <div key={a.externalId} className="space-y-1">
                    <button
                      type="button"
                      disabled={!canQueue}
                      aria-pressed={canQueue ? isSelected : undefined}
                      aria-label={canQueue ? `Select ${a.title ?? a.externalId}` : undefined}
                      onClick={() => toggle(a.externalId)}
                      className={`block w-full overflow-hidden rounded-md ring-offset-2 ${
                        isSelected ? 'ring-2 ring-blue-600' : ''
                      } ${canQueue ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      {a.thumbUrl ? (
                        <img
                          src={a.thumbUrl}
                          alt={a.title ?? ''}
                          loading="lazy"
                          className="aspect-square w-full bg-slate-100 object-cover"
                        />
                      ) : (
                        <div className="aspect-square w-full bg-slate-100" />
                      )}
                    </button>
                    <div className="truncate text-xs font-medium" title={a.title ?? undefined}>
                      {a.title || 'Untitled'}
                    </div>
                    <div className="flex items-center justify-between gap-1">
                      <ArtworkStateBadge artworkId={a.artworkId} status={a.ingestStatus} expired={!!a.expiredAt} />
                      <a
                        href={a.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-slate-400 hover:text-slate-700"
                      >
                        #{a.externalId}
                      </a>
                    </div>
                    {a.ingestStatus === 'failed' && a.ingestError && (
                      <div className="line-clamp-2 text-[11px] text-red-700" title={a.ingestError}>
                        {a.ingestError}
                      </div>
                    )}
                    {a.ingestStatus === 'queued' && (
                      <button
                        type="button"
                        className="text-[11px] text-blue-700 hover:underline"
                        onClick={() => unqueue.mutate([a.externalId])}
                      >
                        Remove from queue
                      </button>
                    )}
                    {!a.artworkId && a.candidateArtworkId && (
                      <div className="space-y-1 rounded-md border border-dashed border-amber-400 bg-amber-50 p-1.5">
                        <Link
                          to={`/app/artworks/${a.candidateArtworkId}`}
                          target="_blank"
                          className="block text-[11px] text-amber-900 underline"
                        >
                          Looks like one we hold — compare
                        </Link>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            disabled={resolve.isPending}
                            className="rounded bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-800 shadow-sm hover:bg-slate-50"
                            onClick={() => resolve.mutate({ artworkExternalId: a.externalId, held: true })}
                          >
                            Same piece
                          </button>
                          <button
                            type="button"
                            disabled={resolve.isPending}
                            className="rounded bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-800 shadow-sm hover:bg-slate-50"
                            onClick={() => resolve.mutate({ artworkExternalId: a.externalId, held: false })}
                          >
                            Different
                          </button>
                        </div>
                      </div>
                    )}
                    {a.artworkId && (
                      <Link to={`/app/artworks/${a.artworkId}`} className="text-[11px] text-blue-700 hover:underline">
                        Open our artwork
                      </Link>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
